import { createContext, useContext, useEffect, useRef } from "react";

import { FloatingPortal } from "@floating-ui/react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faChevronRight,
  faDiagramNested,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { NavLink, type NavLinkProps, type To } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import RetoolIcon from "src/assets/RetoolMenuIcon.svg?react";
import { PopoverProvider } from "src/base-components/Popover/PopoverContext";
import { Icon, type IconSize } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FolderList } from "src/flowsOverview/v2/FolderList";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { useLatestViewedFolder } from "src/flowsOverview/v2/useLatestViewedFolder";
import {
  HoverPopoverLockProvider,
  useHoverPopover,
} from "src/hooks/useHoverPopover";
import { buildNavLinkClickHandler } from "src/layout/navLinkClick";
import { getUrlToWsDashboard, WsDashboardPageCode } from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { useCurrentUserId } from "src/store/AuthStore";

type CollapsedContextProps = {
  collapsed: boolean;
  isDragHovering: boolean;
};
const CollapsedContext = createContext<CollapsedContextProps | undefined>(
  undefined,
);
const useCollapsed = (): CollapsedContextProps => {
  const context = useContext(CollapsedContext);
  if (!context) {
    throw new Error(
      `The NavSideBar collapsed context must be used within a NavSideBar`,
    );
  }
  return context;
};

type CustomNavLinkProps = NavLinkProps & {
  disabled?: boolean;
};

export type RetoolNavItemProps = {
  dataLoc: string;
  retoolPageUrl: string;
  retoolConnectionName: string;
  isCurrentPageRetool: boolean;
  label: string;
};

export const RetoolNavItem: React.FC<RetoolNavItemProps> = ({
  dataLoc,
  retoolPageUrl,
  retoolConnectionName,
  isCurrentPageRetool,
  label,
}) => {
  const { collapsed } = useCollapsed();
  return (
    <Tooltip
      body={retoolConnectionName}
      disabled={!collapsed}
      placement="right"
      title={collapsed ? label : undefined}
      asChild
    >
      <li className="shrink-0 cursor-default">
        <CustomNavLink
          className={twJoin(
            "mr-2.5 flex items-center gap-x-2 rounded-lg px-2 py-1.5",
            isCurrentPageRetool ? "bg-gray-100" : "",
            "hover:bg-gray-50",
          )}
          data-loc={dataLoc}
          end={false}
          to={retoolPageUrl}
          onClick={buildNavLinkClickHandler(isCurrentPageRetool)}
        >
          <RetoolIcon className="m-1 h-3 w-3" />
          {!collapsed && (
            <span className="font-inter-medium-13px text-gray-800">
              {label}
            </span>
          )}
        </CustomNavLink>
      </li>
    </Tooltip>
  );
};

const CustomNavLink: React.FC<CustomNavLinkProps> = ({ disabled, ...rest }) => {
  if (disabled) {
    const disabledState = {
      isActive: false,
      isPending: false,
      isTransitioning: false,
    } as const;
    return (
      <span
        className={
          typeof rest.className === "function"
            ? rest.className(disabledState)
            : rest.className
        }
      >
        {typeof rest.children === "function"
          ? rest.children(disabledState)
          : rest.children}
      </span>
    );
  }

  return <NavLink {...rest} />;
};

type NavItemProps = {
  title: string;
  message?: string;
  to: To;
  icon: IconProp;
  disabled?: boolean;
  dataLoc?: string;
  neverShowActive?: boolean;
  end?: boolean;
  isActiveOverride?: boolean;
  iconSize?: IconSize;
  externalLink?: boolean;
  isCurrentPage?: boolean;
};

export const NavItem: React.FC<NavItemProps> = ({
  disabled,
  dataLoc,
  title,
  message,
  to,
  icon,
  iconSize = "xs",
  neverShowActive = false,
  end = true,
  isActiveOverride,
  externalLink = false,
  isCurrentPage = false,
}) => {
  const { collapsed } = useCollapsed();
  const linkContent = (
    <>
      <Icon
        icon={icon}
        size={iconSize}
        variant={disabled ? "disabled" : "default"}
      />
      {!collapsed && (
        <span
          className={twJoin(
            "font-inter-medium-13px",
            disabled ? "pointer-events-none text-gray-400" : "text-gray-800",
          )}
        >
          {title}
        </span>
      )}
    </>
  );
  return (
    <Tooltip
      body={message}
      disabled={!(collapsed || Boolean(message))}
      placement="right"
      title={collapsed ? title : undefined}
      asChild
    >
      <li className="shrink-0 cursor-default">
        {externalLink ? (
          <a
            href={to as string}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5 hover:bg-gray-50"
            onClick={(e) => e.stopPropagation()}
          >
            {linkContent}
          </a>
        ) : (
          <CustomNavLink
            className={({ isActive }) =>
              twJoin(
                "mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5",
                (isActiveOverride || (isActive && !neverShowActive)) &&
                  "bg-gray-100",
                disabled ? "cursor-default" : "hover:bg-gray-50",
              )
            }
            data-loc={dataLoc}
            disabled={disabled}
            end={end}
            to={to}
            onClick={buildNavLinkClickHandler(isCurrentPage)}
          >
            {linkContent}
          </CustomNavLink>
        )}
      </li>
    </Tooltip>
  );
};

export const DecisionFlowsNavItem: React.FC<{
  orgId: string;
  wsId: string;
}> = ({ orgId, wsId }) => {
  const { isAuthoringPage, isFlowPage, isFlowsListPage } = useCurrentPage();
  const { collapsed, isDragHovering } = useCollapsed();
  const [foldersVisible, setFoldersVisible] = useLocalStorage(
    "sidebarFoldersVisible",
    false,
  );
  const toggleFoldersVisible = () => {
    setFoldersVisible(!foldersVisible);
  };

  const isOnFlowsPage = isFlowsListPage || isFlowPage;
  // Show the `FolderList` automatically when navigating to the flows page.
  const prevIsOnFlowsPageRef = useRef(false);
  useEffect(() => {
    if (isOnFlowsPage && !prevIsOnFlowsPageRef.current) {
      prevIsOnFlowsPageRef.current = true;
      setFoldersVisible(true);
    } else if (!isOnFlowsPage && prevIsOnFlowsPageRef.current) {
      prevIsOnFlowsPageRef.current = false;
    }
  }, [isOnFlowsPage]);

  const foldersResult = useFolders({ workspaceId: wsId });

  const userId = useCurrentUserId();
  const [latestViewedFolder] = useLatestViewedFolder({
    userId: userId!,
    workspaceId: wsId,
  });

  const showInlineFolderList = !collapsed && (foldersVisible || isDragHovering);

  const {
    isOpen: hoverOpen,
    refs: hoverRefs,
    floatingStyles: hoverFloatingStyles,
    getReferenceProps: getHoverReferenceProps,
    getFloatingProps: getHoverFloatingProps,
    lockApi: hoverLockApi,
  } = useHoverPopover({ enabled: collapsed });

  const handleFoldersVisibleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFoldersVisible();
  };

  return (
    <>
      <li
        ref={hoverRefs.setReference}
        className={twJoin(
          "flex shrink cursor-default flex-col overflow-x-visible",
          showInlineFolderList &&
            foldersResult.data &&
            foldersResult.data.folders.length > 3
            ? "min-h-[192px]"
            : "min-h-min",
        )}
        {...getHoverReferenceProps()}
      >
        <div>
          <CustomNavLink
            aria-label={collapsed ? "Decision Flows" : undefined}
            className={({ isActive }) =>
              twJoin(
                "mr-2.5 flex shrink-0 items-center gap-x-2 rounded-lg",
                collapsed ? "p-1.5" : "py-1.5 pr-2 pl-1.5",
                (isAuthoringPage || isActive) && "bg-gray-100",
                "hover:bg-gray-50",
              )
            }
            data-loc="decision-flows-icon"
            end={false}
            to={getUrlToWsDashboard({
              orgId,
              wsId,
              page: WsDashboardPageCode.Flows,
              folderId: latestViewedFolder,
            })}
            onClick={buildNavLinkClickHandler(isFlowsListPage)}
          >
            <Icon icon={faDiagramNested} size="xs" />
            {!collapsed && (
              <>
                <span className="w-full font-inter-medium-13px text-gray-800">
                  Decision Flows
                </span>
                <div onClick={handleFoldersVisibleClick}>
                  <Icon
                    icon={foldersVisible ? faChevronDown : faChevronRight}
                    size="xs"
                  />
                </div>
              </>
            )}
          </CustomNavLink>
        </div>
        {!collapsed && (
          <AnimatePresence initial={false}>
            {(foldersVisible || isDragHovering) && (
              <m.div
                key="folder-list"
                animate={{ height: "auto", opacity: 1 }}
                className="overflow-hidden pt-2"
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <FolderList
                  displayAsNested
                  folderResult={foldersResult}
                  organizationId={orgId}
                  workspaceId={wsId}
                />
              </m.div>
            )}
          </AnimatePresence>
        )}
      </li>
      {collapsed && (
        <FloatingPortal>
          <div
            ref={hoverRefs.setFloating}
            className={twJoin(
              "z-40 w-60 rounded-lg border border-gray-100 bg-white p-3 shadow-base",
              !hoverOpen && "hidden",
            )}
            data-loc="decision-flows-hover-popover"
            style={hoverFloatingStyles}
            {...getHoverFloatingProps({
              onClick: (e) => e.stopPropagation(),
            })}
          >
            <HoverPopoverLockProvider value={hoverLockApi}>
              <PopoverProvider>
                <div className="mb-2 px-1.5 font-inter-semibold-13px text-gray-800">
                  Decision Flows
                </div>
                <FolderList
                  folderResult={foldersResult}
                  organizationId={orgId}
                  workspaceId={wsId}
                />
              </PopoverProvider>
            </HoverPopoverLockProvider>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

type NavSidebarProps = {
  collapsed: boolean;
  isDragHovering?: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>> | null;
  children: React.ReactNode;
  width?: number;
  onResizeStart?: () => void;
};
export const NavSidebar: React.FC<NavSidebarProps> = ({
  collapsed,
  isDragHovering,
  setCollapsed,
  children,
  width,
  onResizeStart,
}) => {
  const resizable = !collapsed && onResizeStart !== undefined;
  return (
    <aside
      className={twJoin(
        "z-[1] h-full bg-white py-3.5 shadow-base",
        !collapsed && "flex-shrink-0 pl-2.5",
        !collapsed && width === undefined && "w-52",
        collapsed && "pl-2.5",
        resizable && "relative",
      )}
      style={!collapsed && width !== undefined ? { width } : undefined}
    >
      <nav className="flex h-full flex-col" data-loc="flow-subnavigation">
        <CollapsedContext
          value={{ collapsed, isDragHovering: isDragHovering ?? false }}
        >
          {/* Clicks in the main nav section (e.g. the gaps between items) must
              not collapse the sidebar — only the leading/trailing whitespace does. */}
          <ul
            className="decideScrollbarThin flex min-h-0 shrink flex-col gap-y-2 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </ul>
          <div
            className="min-h-[48px] shrink-0 grow"
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed?.((prev) => !prev);
            }}
            data-loc="nav-sidebar-collapse-area"
          />
        </CollapsedContext>
      </nav>
      {resizable && (
        <button
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize"
          onMouseDown={onResizeStart}
          onClick={(e) => e.stopPropagation()}
          data-loc="nav-sidebar-resize-handle"
          aria-label="Resize sidebar"
        />
      )}
    </aside>
  );
};
